/**
 * Delivery contract for interactive tool-outcome cards (web search approval,
 * file upload, selection, publish approval): dispatch an outcome to the
 * assistant with a typed result, and watch the stream to confirm the reply
 * actually arrived. Generalized from WebSearchApproval's machinery.
 */
import axios from "axios";
import { useCallback, useEffect, useRef } from "react";
import { nuclearn_api_url } from "src/App";
import {
  extractErrorDetail,
  isClientRejectionError,
  isConflictError,
} from "src/helpers/apiError";
import { ChatStreamStore } from "./stores/StreamingStore";

/**
 * "sent" = the outcome may have reached the session; `confirmed` says
 * whether it did. "rejected" = no path reached the backend, so the caller
 * may revert and the user may retry.
 */
export type DispatchResult = "sent" | "rejected";

/**
 * "aborted" = nothing to judge: the watcher was disarmed (user Stop,
 * unmount) or the answer resumes no turn. Not a failure.
 */
export type DeliveryConfirmation = "delivered" | "undelivered" | "aborted";

export interface DeliveryResult {
  dispatch: DispatchResult;
  /** Resolves once the stream proves (or disproves) the reply arrived. */
  confirmed: Promise<DeliveryConfirmation>;
  /** Stop watching (component unmount). Resolves `confirmed` as "aborted". */
  disarm: () => void;
}

export type RetryInjectionResult =
  | { kind: "delivered" }
  | { kind: "session-gone" }
  | { kind: "failed"; detail: string; permanent: boolean };

/*
 * Bounded wait for the reconnect, not an upper bound on the SSE layer's own
 * backoff (1s/2s/4s plus attempt latency can exceed it): a timeout only
 * fails this retry attempt fast; the user can retry again.
 */
const CONNECT_WAIT_MS = 8000;

/** Backstop for a hung dispatch that never errs, streams, or terminates. */
const WATCHER_BACKSTOP_MS = 120_000;

/** Resolve once the store reports a live SSE subscription (or terminal error). */
export function waitForConnection(
  store: ChatStreamStore,
  timeoutMs = CONNECT_WAIT_MS,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (store.getState().isConnected) {
      resolve(true);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = store.subscribe((s) => {
      if (!s.isConnected && !s.isConnectionError) return;
      clearTimeout(timer);
      unsubscribe();
      resolve(s.isConnected);
    });
    timer = setTimeout(() => {
      unsubscribe();
      resolve(store.getState().isConnected);
    }, timeoutMs);
  });
}

/*
 * Dispatchers resolve before delivery is proven, so watch the store
 * edge-triggered:
 * - confirm on modelContentCount growth (streamed answers merge into the
 *   last assistant message on these surfaces, so entry counts never move);
 * - confirm on a matching tool_outcome_ack (the server already answered
 *   this prompt — the reply exists);
 * - resolve "aborted" when the user hits Stop (their own cancel is not a
 *   delivery failure);
 * - resolve "undelivered" on a fresh transition into an error state, or
 *   when the terminal flags flip with no content since baseline. `end` is
 *   only a turn boundary and never sets isEnded; `status: completed|error`,
 *   onclose/onerror after max retries, and Stop are what set the terminal
 *   flags;
 * - a backstop (reset on turn activity) covers a dispatch that hangs.
 */
export function armDeliveryWatcher(
  store: ChatStreamStore,
  promptId: string,
): { confirmed: Promise<DeliveryConfirmation>; disarm: () => void } {
  const base = store.getState();
  const baseline = {
    streamError: base.streamError,
    isConnectionError: base.isConnectionError,
    modelContentCount: base.modelContentCount,
    lastUserStopAt: base.lastUserStopAt,
    lastOutcomeAck: base.lastOutcomeAck,
  };
  let sawTurnActivity = base.isTurnActive || !base.isEnded;

  let settle: (value: DeliveryConfirmation) => void;
  const confirmed = new Promise<DeliveryConfirmation>((resolve) => {
    settle = resolve;
  });

  let cleanup: (() => void) | null = null;
  const finish = (value: DeliveryConfirmation, reason?: string) => {
    cleanup?.();
    if (value === "undelivered") {
      console.warn("[deliveryWatcher:demoted]", reason);
    }
    settle(value);
  };

  let backstop = setTimeout(
    () => finish("undelivered", "no stream activity after dispatch"),
    WATCHER_BACKSTOP_MS,
  );
  const unsubscribe = store.subscribe((s) => {
    if (s.lastUserStopAt !== baseline.lastUserStopAt) {
      finish("aborted");
      return;
    }
    if (s.modelContentCount > baseline.modelContentCount) {
      finish("delivered");
      return;
    }
    if (
      s.lastOutcomeAck !== baseline.lastOutcomeAck &&
      s.lastOutcomeAck?.prompt_id === promptId &&
      s.lastOutcomeAck.already_answered
    ) {
      finish("delivered");
      return;
    }
    const freshError =
      (Boolean(s.streamError) && s.streamError !== baseline.streamError) ||
      (s.isConnectionError && !baseline.isConnectionError);
    if (freshError) {
      finish("undelivered", "stream error after dispatch");
      return;
    }
    if (s.isTurnActive || !s.isEnded) {
      sawTurnActivity = true;
      clearTimeout(backstop);
      backstop = setTimeout(
        () => finish("undelivered", "no stream activity after dispatch"),
        WATCHER_BACKSTOP_MS,
      );
      return;
    }
    if (sawTurnActivity) {
      finish("undelivered", "turn concluded without new content");
    }
  });
  cleanup = () => {
    cleanup = null;
    clearTimeout(backstop);
    unsubscribe();
  };

  return { confirmed, disarm: () => finish("aborted") };
}

export interface DeliverToolOutcomeArgs {
  dialogueId?: number;
  promptId: string;
  /** Spread into `tool_outcome: {prompt_id, ...toolOutcome}` on injection. */
  toolOutcome?: Record<string, unknown>;
  /**
   * Full injection-body override for legacy shapes (e.g. selection prompts'
   * `selection` key). Wins over toolOutcome. With neither, the injection leg
   * is skipped (publish approvals: continuation/fallback only).
   */
  injectionBody?: Record<string, unknown>;
  /** User-voiced fallback for old backends and the legacy resume paths. */
  fallbackMessage: string;
  /** Outcome patched into the part — continueConversation can carry it. */
  patched: boolean;
  /**
   * Whether a successful injection resumes the assistant's turn (default
   * true). False for a non-final questionnaire answer: the backend holds the
   * turn until every prompt is answered, so nothing streams yet and the
   * typing indicator must stay dark.
   */
  resumesTurn?: boolean;
}

/** A result with nothing left to watch. */
const settled = (
  dispatch: DispatchResult,
  confirmation: DeliveryConfirmation,
): DeliveryResult => ({
  dispatch,
  confirmed: Promise.resolve(confirmation),
  disarm: () => {},
});

const rejectedResult = () => settled("rejected", "undelivered");

/*
 * tool_outcome injection first; only on a 4xx rejection or with no live SSE
 * does it cascade to patched-dialogue resume, then a user message. A non-4xx
 * injection failure (5xx/timeout) stops the cascade — the turn may already
 * have resumed, so resending is not safe and the outcome is unknown, not
 * failed: the result is "sent" with a live watcher to prove it either way.
 */
export async function deliverToolOutcome(
  store: ChatStreamStore,
  args: DeliverToolOutcomeArgs,
): Promise<DeliveryResult> {
  const {
    dialogueId,
    promptId,
    toolOutcome,
    injectionBody,
    fallbackMessage,
    patched,
    resumesTurn = true,
  } = args;
  const actions = store.getState();
  const body =
    injectionBody ??
    (toolOutcome
      ? { tool_outcome: { prompt_id: promptId, ...toolOutcome } }
      : null);
  // Inject only while this client holds the SSE subscription — a 202
  // otherwise streams the reply to a channel nobody is subscribed to.
  if (dialogueId && body && actions.isConnected) {
    try {
      await axios.post(`${nuclearn_api_url}/chat/send/${dialogueId}`, {
        // `content` is a best-effort fallback only for backends predating
        // the structured outcome fields — new ones never read it.
        content: fallbackMessage,
        type: "message",
        ...body,
      });
      // A parked answer streams nothing, so there is nothing to watch: no
      // typing indicator and no watcher (its only exit would be a false
      // backstop demotion).
      if (!resumesTurn) return settled("sent", "aborted");
      // The reply streams with no user_message event; light the typing
      // indicator ourselves.
      actions.markTurnActive();
      return { dispatch: "sent", ...armDeliveryWatcher(store, promptId) };
    } catch (err) {
      // Any 4xx is rejected before the outcome reaches the session, so
      // resending is safe; a 5xx/timeout may already have resumed the turn.
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const detail = extractErrorDetail(err, "Injection failed");
      if (!isClientRejectionError(err)) {
        console.error("[deliverToolOutcome:injection-failed]", {
          dialogueId,
          promptId,
          status,
          detail,
        });
        // Unknown, not failed: the watcher confirms on content growth or
        // demotes honestly. A parked answer resumes nothing, so a resend
        // is safe there and the caller may revert.
        return resumesTurn
          ? { dispatch: "sent", ...armDeliveryWatcher(store, promptId) }
          : rejectedResult();
      }
      console.warn("[deliverToolOutcome:injection-rejected] falling back", {
        dialogueId,
        promptId,
        status,
        detail,
      });
    }
  }
  // Fallback paths set their own turn state via fetchFromSSE.
  const dispatched = patched ? await actions.continueConversation() : false;
  if (!dispatched && !(await actions.sendUserMessage(fallbackMessage))) {
    return rejectedResult();
  }
  return { dispatch: "sent", ...armDeliveryWatcher(store, promptId) };
}

/*
 * Retry ships an already-settled outcome by injection only. The server is
 * content-idempotent, not turn-idempotent — the session skips the duplicate
 * turn when it already ran one for this prompt (and emits tool_outcome_ack
 * so the skip is observable) — and a 409 means "no session now", not "the
 * server never acted", so retry never restarts a conversation: the durable
 * annotation is what carries the outcome into the next turn instead.
 */
export async function retryToolOutcomeInjection(
  store: ChatStreamStore,
  args: {
    dialogueId: number;
    promptId: string;
    toolOutcome?: Record<string, unknown>;
    injectionBody?: Record<string, unknown>;
    fallbackMessage: string;
  },
): Promise<RetryInjectionResult> {
  const { dialogueId, promptId, toolOutcome, injectionBody, fallbackMessage } =
    args;
  const body =
    injectionBody ??
    (toolOutcome
      ? { tool_outcome: { prompt_id: promptId, ...toolOutcome } }
      : null);
  if (!body) {
    return { kind: "failed", detail: "Nothing to inject", permanent: true };
  }
  if (!store.getState().isConnected) {
    // Full DB replay — a skipReplay connect would truncate the transcript.
    const state = store.getState();
    if (state.lastDialogueId) {
      state.retryConnection();
    } else {
      state.connectToStream(dialogueId, { replay: true });
    }
    if (!(await waitForConnection(store))) {
      return {
        kind: "failed",
        detail: "Could not reconnect to the chat stream",
        permanent: false,
      };
    }
  }
  try {
    await axios.post(`${nuclearn_api_url}/chat/send/${dialogueId}`, {
      content: fallbackMessage,
      type: "message",
      ...body,
    });
    store.getState().markTurnActive();
    return { kind: "delivered" };
  } catch (err) {
    if (isConflictError(err)) {
      return { kind: "session-gone" };
    }
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    const detail = extractErrorDetail(err, "Delivery failed");
    console.error("[retryToolOutcomeInjection:failed]", { status, detail });
    // 400/401/403/404/422 are pre-command rejections that will never succeed.
    return {
      kind: "failed",
      detail,
      permanent:
        status !== undefined && [400, 401, 403, 404, 422].includes(status),
    };
  }
}

/**
 * Own a card's delivery watcher: at most one live per card, the previous
 * one disarmed on re-arm and the live one on unmount. Returns the verdict
 * promise for the caller to act on.
 */
export function useDeliveryWatcher() {
  const disarmRef = useRef<(() => void) | null>(null);
  useEffect(() => () => disarmRef.current?.(), []);
  return useCallback(
    (watcher: Pick<DeliveryResult, "confirmed" | "disarm">) => {
      disarmRef.current?.();
      disarmRef.current = watcher.disarm;
      return watcher.confirmed;
    },
    [],
  );
}
